import { InputAdornment } from '@mui/material';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import { FC, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { getCurrencySymbol } from '../../utils';

type Props = Pick<
  OutlinedTextFieldProps,
  | 'name'
  | 'id'
  | 'fullWidth'
  | 'size'
  | 'placeholder'
  | 'disabled'
  | 'error'
  | 'helperText'
  | 'slotProps'
  | 'onFocus'
> & {
  value?: number | null;
  min?: number;
  max?: number;
  decimals?: number;
  currency?: string;
  currencyStartAdornment?: boolean;
  percentageEndAdornment?: boolean;
  onChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  hideZeroValue?: boolean;
  showPercentSuffix?: boolean;
  suffix?: string;
  clearZeroValueOnFocus?: boolean;
  fixedDecimalScale?: boolean;
  textAlign?: 'left' | 'right';
};

const NumberInput: FC<Props> = ({
  decimals = 0,
  size = 'small',
  value,
  onChange,
  onBlur,
  currency,
  hideZeroValue = false,
  currencyStartAdornment = false,
  percentageEndAdornment = false,
  showPercentSuffix = false,
  suffix,
  clearZeroValueOnFocus = false,
  fixedDecimalScale = false,
  textAlign,
  min,
  max,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState<Props['value'] | ''>(value);
  const hasChangedRef = useRef(false);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const getValue = (value: string) => {
    const rawValue =
      currency && !currencyStartAdornment
        ? value.replace(getCurrencySymbol(currency), '')
        : value;

    const activeSuffix = suffix ?? (showPercentSuffix ? '%' : '');
    return Number(rawValue.replace(activeSuffix, '').replace(/,/g, ''));
  };

  return (
    <NumericFormat
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      value={hideZeroValue ? displayValue || '' : displayValue}
      customInput={TextField}
      prefix={
        currency && !currencyStartAdornment
          ? getCurrencySymbol(currency)
          : undefined
      }
      type="text"
      fixedDecimalScale={fixedDecimalScale ?? !!currency}
      decimalScale={decimals}
      allowNegative={min === undefined || min < 0}
      allowLeadingZeros={false}
      isAllowed={({ floatValue }) => {
        if (floatValue === undefined) return true;
        if (min !== undefined && floatValue < min) return false;
        if (max !== undefined && floatValue > max) return false;
        return true;
      }}
      suffix={suffix ?? (showPercentSuffix ? '%' : undefined)}
      onFocus={() => {
        if (clearZeroValueOnFocus && !value) {
          hasChangedRef.current = false;
          setDisplayValue('');
        }
      }}
      onChange={(event) => {
        hasChangedRef.current = true;
        onChange?.(getValue(event.target.value));
      }}
      onBlur={(event) => {
        if (clearZeroValueOnFocus && !hasChangedRef.current) {
          setDisplayValue(value);
        } else {
          onBlur?.(getValue(event.target.value));
        }

        hasChangedRef.current = false;
      }}
      thousandSeparator
      size={size}
      variant="outlined"
      {...rest}
      slotProps={{
        ...(rest.slotProps && {
          ...rest.slotProps,
        }),
        htmlInput: {
          ...(typeof rest.slotProps?.htmlInput === 'object'
            ? rest.slotProps.htmlInput
            : {}),
          ...(textAlign && { style: { textAlign } }),
        },
        input: {
          ...(typeof rest.slotProps?.input === 'object'
            ? rest.slotProps.input
            : {}),
          startAdornment:
            currencyStartAdornment && currency ? (
              <InputAdornment position="start">
                {getCurrencySymbol(currency)}
              </InputAdornment>
            ) : typeof rest.slotProps?.input === 'object' ? (
              rest.slotProps.input?.startAdornment
            ) : undefined,
          endAdornment: percentageEndAdornment ? (
            <InputAdornment position="end">%</InputAdornment>
          ) : typeof rest.slotProps?.input === 'object' ? (
            rest.slotProps.input?.endAdornment
          ) : undefined,
        },
      }}
    />
  );
};

export default NumberInput;
